import './accordion.form-container.scss';

import * as React from 'react';

import {
  AddressType,
  BdmTerritoryEdges,
  ContactType,
  UserGroup,
  UwTerritoryEdges,
} from '@converge/graphql';
import {
  CommissionBrokerage,
  CreateBrokerageFields,
} from './commission-container';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from '@mui/material/AccordionSummary';
import { SubmitHandler, UseFormReturn } from 'react-hook-form';
import TeamsList, { ITeamUser } from './teams-list';
import { LicensePaginationSort } from '@converge/graphql';
import { useSearchParams } from 'react-router-dom';

import AgentSyncAccordions from '../entity/broker-agencies/broker-form/accordion.agentsync-container';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LicenseList from './license-list';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import OfficeList from './office-list';
import ProducerBrokerageInfo from './producer-brokerage-info';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  backgroundColor: 'white',
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper': {
    marginLeft: 'auto', // Align the expand icon to the right
    transform: 'rotate(0deg)', // Initial rotation
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  paddingLeft: '12px',
}));

export interface ILicenses {
  __typename?: 'HasLicenseTypeEdge' | undefined;
  cursor: number;
  node: {
    __typename?: 'HasLicenseType' | undefined;
    uuid?: string | null | undefined;
    licenseNumber?: string | null | undefined;
    expirationDate?: Date;
    source?: string | null;
    licenseType?: string | null;
    perpetual?: boolean | null;
    licenseHolderUuid?: string;
    region?: {
      __typename?: 'Region' | undefined;
      name?: string | null | undefined;
      uuid?: string | null | undefined;
      iso2Code?: string | null | undefined;
    } | null;
  };
}

export interface ITeams {
  __typename?: 'TeamEdge' | undefined;
  node: {
    __typename?: 'Team' | undefined;
    uuid: string;
    name?: string | null | undefined;
    uwTerritories: UwTerritoryEdges;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    persons?: IPerson | any;
    users?: ITeamUser;
    bdmTerritories: BdmTerritoryEdges;
  };
}

export interface IPerson {
  __typename?: 'PersonEdge' | undefined;
  cursor: number;
  node: {
    __typename?: 'Person' | undefined;
    uuid?: string | null | undefined;
    agent?: boolean | null | undefined;
    firstName?: string | null | undefined;
    lastName?: string | null | undefined;
    source?: string | null | undefined;
    teamLead?: boolean | null;
    primary?: boolean;
    contacts?: {
      __typename?: 'ContactEdges' | undefined;
      edges: Array<{
        __typename?: 'ContactEdge' | undefined;
        cursor: number;
        node: {
          __typename?: 'Contact' | undefined;
          uuid?: string | null | undefined;
          type?: string | null | undefined;
          value?: string | null | undefined;
          primary: boolean;
        };
      }>;
    };
  };
}
export interface IOffices {
  __typename?: 'OfficeEdge';
  node: {
    __typename?: 'Office';
    uuid?: string | null;
    name?: string | null;
    useCorpAddress?: boolean | null | undefined;
    persons: {
      __typename?: 'PersonEdges';
      edges: Array<{
        __typename?: 'PersonEdge';
        cursor: number;
        node: {
          __typename?: 'Person';
          uuid?: string | null | undefined;
          primary: boolean;
          firstName?: string | null;
          lastName?: string | null;
          primaryContact?: boolean | null;
          personType?: {
            __typename?: 'HasPerson';
            primary?: boolean | null;
          } | null;
          contacts: {
            __typename?: 'ContactEdges';
            edges: Array<{
              __typename?: 'ContactEdge';
              cursor: number;
              node: {
                __typename?: 'Contact';
                uuid?: string | null | undefined;
                type?: ContactType | null;
                value?: string | null;
                source?: string | null;
                primary: boolean;
              };
            }>;
          };
        };
      }>;
    };
    users: {
      __typename?: 'UserEdges';
      edges: Array<{
        __typename?: 'UserEdge';
        cursor: number;
        node: {
          __typename?: 'User';
          uuid?: string | null | undefined;
          username?: string | null;
          userId?: string | null;
          groups?: UserGroup[] | null;
          type?: UserGroup | null;
        };
      }>;
    };
    address?: {
      __typename?: 'Address';
      uuid: string;
      street1?: string | null;
      street2?: string | null;
      city?: string | null;
      type?: AddressType | null;
      postalCode?: string | null;
      source?: string | null;
      primary: boolean;
      regions: {
        __typename?: 'RegionEdges';
        edges: Array<{
          __typename?: 'RegionEdge';
          cursor: number;
          node: {
            __typename?: 'Region';
            uuid?: string | null;
            name?: string | null;
            iso2Code?: string | null;
          };
        }>;
      };
      countries: {
        __typename?: 'CountryEdges';
        edges: Array<{
          __typename?: 'CountryEdge';
          cursor: number;
          node: {
            __typename?: 'Country';
            uuid: string;
            name?: string | null;
            iso2Code?: string | null;
          };
        }>;
      };
    } | null;
    uwTerritories: {
      __typename?: 'UwTerritoryEdges';
      edges: Array<{
        __typename?: 'UwTerritoryEdge';
        node: {
          __typename?: 'UwTerritory';
          uuid: string;
          name?: string | null;
        };
      }>;
    } | null;
    bdmTerritories: {
      __typename?: 'BdmTerritoryEdge'; // Note: Edge vs Edges
      edges: Array<{
        __typename?: 'BdmTerritory'; // Note: Edge vs Edges
        node: {
          __typename?: 'BdmTerritory';
          uuid: string;
          name?: string | null;
        };
      }>;
    } | null;
  };
}

export interface primaryBrokerageAddress {
  primaryRegion: string;
  primaryAddressUuid: string;
  primaryAddress1: string;
  primaryAddress2: string;
  primaryCity: string;
  primaryCountry: string;
  primaryPostalCode: string;
}
export interface BrokageFormAccordionsProps {
  setIsSameAddress: React.Dispatch<React.SetStateAction<boolean>>;
  isSameAddress: boolean;
  formMethods: UseFormReturn<CreateBrokerageFields, unknown>;
  setIsLicenseBrokerage: React.Dispatch<React.SetStateAction<boolean>>;

  setIsBrokerageSave?: React.Dispatch<React.SetStateAction<boolean>>;
  onSubmitHandler: () => SubmitHandler<CreateBrokerageFields>;
  brokerageUuid: string | undefined;
  brokerageLicense: ILicenses[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  brokerageTeams: ITeams[] | any[];
  brokers: IPerson[];
  offices: IOffices[];
  isReadOnly?: boolean;
  primaryBrokerageAddress: primaryBrokerageAddress;
  brokerageBillingStreet1?: string | null;
  brokerageBillingStreet2?: string | null;
  brokerageBillingCity?: string | null;
  brokerageBillingPostalCode?: string | null;
  brokerageBillingCountry?: string | null;
  brokerageBillingRegion?: string | null;
  onLicenseSortChange?: (sortModel: LicensePaginationSort) => void;
  parentBrokerageName?: string | null;
  partnerBrokerageName?: string | null;
  isLoading?: boolean;
}
export default function BrokageFormAccordions({
  setIsSameAddress,
  setIsLicenseBrokerage,
  formMethods,
  isSameAddress,
  brokerageLicense,
  onSubmitHandler,
  brokerageUuid,
  brokerageTeams,
  brokers,
  offices,
  primaryBrokerageAddress,
  isReadOnly,
  brokerageBillingStreet1,
  brokerageBillingStreet2,
  brokerageBillingCity,
  brokerageBillingPostalCode,
  brokerageBillingCountry,
  brokerageBillingRegion,
  onLicenseSortChange,
  parentBrokerageName,
  partnerBrokerageName,
  isLoading = false,
}: BrokageFormAccordionsProps) {
  const [searchParams] = useSearchParams();
  const sectionParam = searchParams.get('section');

  // State for each individual panel
  const [isPanel01pen, setPanel01pen] = React.useState(true); // First panel open by default
  const [isPanel02pen, setPanel02pen] = React.useState(true);
  const [isPanel03pen, setPanel03pen] = React.useState(false);
  const [isPanel04pen, setPanel04pen] = React.useState(
    sectionParam === 'office'
  );
  const [isPanel05pen, setPanel05pen] = React.useState(sectionParam === 'team');
  const accordionRef = React.useRef<HTMLDivElement | null>(null);

  // Triggered when a user clicks the inherited BDM name in the broker form tooltip, which navigates here with ?section=office or ?section=team.
  // The matching accordion is pre-expanded on mount and scrolled into view after a short delay to allow the DOM to settle.

  const officeAccordionRef = React.useRef<HTMLDivElement | null>(null);
  const teamAccordionRef = React.useRef<HTMLDivElement | null>(null);

  const scrollToElement = (el: HTMLDivElement | null) => {
    if (!el) return;
    const container = document.querySelector('.application-body-wrapper');
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    container.scrollTop += elRect.top - containerRect.top - 80;
  };

  React.useEffect(() => {
    if (sectionParam !== 'office' && sectionParam !== 'team') return;
    const timer = setTimeout(() => {
      if (sectionParam === 'office')
        scrollToElement(officeAccordionRef.current);
      if (sectionParam === 'team') scrollToElement(teamAccordionRef.current);
    }, 600);
    return () => clearTimeout(timer);
  }, [sectionParam]);

  // Handler to toggle each panel
  const handleChangePanel1 = (
    event: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setPanel01pen(newExpanded);
  };

  const handleChangePanel2 = (
    event: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setPanel02pen(newExpanded);
  };

  const handleChangePanel3 = (
    event: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setPanel03pen(newExpanded);
  };

  const handleChangePanel4 = (
    event: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setPanel04pen(newExpanded);
  };

  const handleChangePanel5 = (
    event: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setPanel05pen(newExpanded);
  };

  const handleSubmitFromChild = async () => {
    const isValid = await formMethods.trigger(); // Trigger form validation

    if (isValid) {
      // Instead of manually calling onSubmitHandler, use formMethods.handleSubmit to trigger form submission
      formMethods.handleSubmit(onSubmitHandler())(); // This triggers the form submission process
    }
  };

  return (
    <div className="from-wrapper">
      <div>{!brokerageUuid && <AgentSyncAccordions />}</div>
      <div className="accordion">
        <Accordion
          expanded={isPanel01pen}
          onChange={handleChangePanel1}
          className="panel"
        >
          <AccordionSummary
            expandIcon={
              <ExpandMoreIcon
                className="expand-icon"
                sx={{
                  transform: isPanel01pen ? 'rotate(270deg)' : 'rotate(-90deg)',
                }}
              />
            }
            aria-controls="panel1d-content"
            id="panel1d-header"
          >
            <Typography className="title">Brokerage Information</Typography>
          </AccordionSummary>
          <AccordionDetails className="broker-info">
            <div className="brokerage-form-wrapper">
              <ProducerBrokerageInfo
                setIsSameAddress={setIsSameAddress}
                setIsLicenseBrokerage={setIsLicenseBrokerage}
                formMethods={formMethods}
                isSameAddress={isSameAddress}
                isReadOnly={isReadOnly}
                brokers={brokers}
                brokerageBillingStreet1={brokerageBillingStreet1}
                brokerageBillingStreet2={brokerageBillingStreet2}
                brokerageBillingCity={brokerageBillingCity}
                brokerageBillingPostalCode={brokerageBillingPostalCode}
                brokerageBillingCountry={brokerageBillingCountry}
                brokerageBillingRegion={brokerageBillingRegion}
                parentBrokerageName={parentBrokerageName}
                partnerBrokerageName={partnerBrokerageName}
              />
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
      <div className="accordion">
        <Accordion
          expanded={isPanel02pen && !isReadOnly}
          onChange={handleChangePanel2}
          className="panel"
          ref={accordionRef}
        >
          <AccordionSummary
            expandIcon={
              <ExpandMoreIcon
                className="expand-icon"
                sx={{
                  transform: isPanel02pen ? 'rotate(270deg)' : 'rotate(-90deg)',
                }}
              />
            }
            aria-controls="panel1d-content"
            id="panel1d-header"
          >
            <Typography className="title">Commission</Typography>
          </AccordionSummary>
          <AccordionDetails className="commission-info">
            <div className="commission">
              <CommissionBrokerage formMethods={formMethods} />
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
      <div className="accordion">
        <Accordion
          expanded={isPanel03pen && !isReadOnly}
          onChange={handleChangePanel3}
          className="panel"
        >
          <AccordionSummary
            aria-controls="panel1d-content"
            id="panel1d-header"
            expandIcon={
              <ExpandMoreIcon
                className="expand-icon"
                sx={{
                  transform: isPanel03pen ? 'rotate(270deg)' : 'rotate(-90deg)',
                }}
              />
            }
          >
            <Typography className="title">Licenses</Typography>
          </AccordionSummary>

          <AccordionDetails>
            <Typography>
              <div className="license-list">
                <LicenseList
                  handleSubmitFromChild={handleSubmitFromChild}
                  brokerageLicense={brokerageLicense}
                  brokerageUuid={brokerageUuid}
                  onSortChange={onLicenseSortChange}
                  isLoading={isLoading}
                />
              </div>
            </Typography>
          </AccordionDetails>
        </Accordion>
      </div>
      <div className="accordion" ref={officeAccordionRef}>
        <Accordion
          expanded={isPanel04pen && (!isReadOnly || sectionParam === 'office')}
          onChange={handleChangePanel4}
          className="panel"
        >
          <AccordionSummary
            aria-controls="panel1d-content"
            id="panel1d-header"
            expandIcon={
              <ExpandMoreIcon
                className="expand-icon"
                sx={{
                  transform: isPanel04pen ? 'rotate(270deg)' : 'rotate(-90deg)',
                }}
              />
            }
          >
            <Typography className="title">Offices</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              <div className="office-list">
                <OfficeList
                  handleSubmitFromChild={handleSubmitFromChild}
                  offices={offices}
                  brokerageUuid={brokerageUuid}
                  primaryBrokerageAddress={primaryBrokerageAddress}
                  brokers={brokers}
                />
              </div>
            </Typography>
          </AccordionDetails>
        </Accordion>
      </div>
      <div className="accordion team-accordion" ref={teamAccordionRef}>
        <Accordion
          expanded={isPanel05pen && (!isReadOnly || sectionParam === 'team')}
          onChange={handleChangePanel5}
          className="panel"
        >
          <AccordionSummary
            aria-controls="panel1d-content"
            id="panel1d-header"
            expandIcon={
              <ExpandMoreIcon
                className="expand-icon"
                sx={{
                  transform: isPanel05pen ? 'rotate(270deg)' : 'rotate(-90deg)',
                }}
              />
            }
          >
            <Typography className="title">Teams</Typography>
          </AccordionSummary>

          <AccordionDetails>
            <Typography>
              <div className="team-list">
                <TeamsList
                  handleSubmitFromChild={handleSubmitFromChild}
                  brokerageTeams={brokerageTeams}
                  brokerageUuid={brokerageUuid}
                  brokers={brokers}
                />
              </div>
            </Typography>
          </AccordionDetails>
        </Accordion>
      </div>
    </div>
  );
}
